import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Submissions.css';
import '../components/AuthWarningModal.css';
import { 
  IconEdit, 
  IconEye, 
  IconSearch, 
  IconChevronLeft, 
  IconChevronRight, 
  IconChevronsLeft, 
  IconChevronsRight,
  IconChevronDown,
  IconX
} from '@tabler/icons-react';

function Submissions() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [resending, setResending] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [messageModalContent, setMessageModalContent] = useState({ title: '', message: '' });
  const [pendingAction, setPendingAction] = useState(null); // Store pending submission ID and action type
  
  // Tab state
  const [activeTab, setActiveTab] = useState('submissions');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  
  // Filter & Search state
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchUserId, setSearchUserId] = useState('');
  
  // Sorting state
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/receipts/history');
      if (response.data.success) {
        setSubmissions(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch submissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  const formatAmount = (amount) => {
    return `MYR ${amount.toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      processing: 'status-processing',
      processed: 'status-processed',
      approved: 'status-approved',
      rejected: 'status-rejected',
      failed: 'status-failed',
      partial_failed: 'status-partial-failed',
      reward_failed: 'status-reward-failed'
    };
    return statusClasses[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      processing: 'Processing',
      processed: 'Processed',
      approved: 'Approved',
      rejected: 'Rejected',
      failed: 'Failed',
      partial_failed: 'Partial Failed',
      reward_failed: 'Reward Failed'
    };
    return labels[status] || status;
  };

  const hasRewardErrors = (submission) => {
    return submission.rewards?.voucherError || submission.rewards?.tokenError;
  };

  const canResend = (submission) => {
    return ['partial_failed', 'reward_failed', 'failed'].includes(submission.status) && 
           hasRewardErrors(submission);
  };

  // Check if submission can be approved/rejected (pending manual review)
  const canReview = (submission) => {
    return submission.status === 'pending' && submission.awaitingReview === true;
  };

  // Handle approve submission
  const handleApprove = (submissionId) => {
    setPendingAction({ type: 'approve', id: submissionId });
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    if (!pendingAction || pendingAction.type !== 'approve') return;
    const submissionId = pendingAction.id;
    
    setShowApproveModal(false);
    setApproving(true);
    try {
      const response = await api.post(`/receipts/${submissionId}/approve`, {
        reviewedBy: 'admin'
      });
      
      if (response.data.success) {
        // Update the submission in the list
        setSubmissions(prev => prev.map(s => 
          s.id === submissionId ? response.data.data : s
        ));
        // Update selected submission if it's open
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
        setMessageModalContent({ title: 'Success', message: 'Submission approved successfully!' });
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Failed to approve submission:', err);
      setMessageModalContent({ title: 'Error', message: 'Failed to approve submission: ' + (err.response?.data?.error || err.message) });
      setShowMessageModal(true);
    } finally {
      setApproving(false);
      setPendingAction(null);
    }
  };

  // Handle reject submission
  const handleReject = (submissionId) => {
    setPendingAction({ type: 'reject', id: submissionId });
    setRejectReason('');
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!pendingAction || pendingAction.type !== 'reject') return;
    if (!rejectReason.trim()) {
      setMessageModalContent({ title: 'Error', message: 'Please provide a reason for rejection.' });
      setShowMessageModal(true);
      return;
    }
    
    const submissionId = pendingAction.id;
    setShowRejectModal(false);
    setRejecting(true);
    try {
      const response = await api.post(`/receipts/${submissionId}/reject`, {
        reviewedBy: 'admin',
        rejectReason: rejectReason.trim()
      });
      
      if (response.data.success) {
        // Update the submission in the list
        setSubmissions(prev => prev.map(s => 
          s.id === submissionId ? response.data.data : s
        ));
        // Update selected submission if it's open
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
        setMessageModalContent({ title: 'Success', message: 'Submission rejected.' });
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Failed to reject submission:', err);
      setMessageModalContent({ title: 'Error', message: 'Failed to reject submission: ' + (err.response?.data?.error || err.message) });
      setShowMessageModal(true);
    } finally {
      setRejecting(false);
      setPendingAction(null);
      setRejectReason('');
    }
  };

  const handleResend = (submissionId) => {
    setPendingAction({ type: 'resend', id: submissionId });
    setShowResendModal(true);
  };

  const confirmResend = async () => {
    if (!pendingAction || pendingAction.type !== 'resend') return;
    const submissionId = pendingAction.id;
    
    setShowResendModal(false);
    setResending(true);
    try {
      const response = await api.post(`/receipts/${submissionId}/resend-rewards`, {
        resendBy: 'admin'
      });
      
      if (response.data.success) {
        // Update the submission in the list
        setSubmissions(prev => prev.map(s => 
          s.id === submissionId ? response.data.data : s
        ));
        // Update selected submission if it's open
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
        setMessageModalContent({ title: 'Success', message: 'Rewards resent successfully!' });
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Failed to resend rewards:', err);
      setMessageModalContent({ title: 'Error', message: 'Failed to resend rewards: ' + (err.response?.data?.error || err.message) });
      setShowMessageModal(true);
    } finally {
      setResending(false);
      setPendingAction(null);
    }
  };

  const openDetailModal = (submission) => {
    setSelectedSubmission(submission);
  };

  const closeDetailModal = () => {
    setSelectedSubmission(null);
  };

  const openImageViewer = (imageUrl) => {
    setViewingImage(imageUrl);
  };

  const closeImageViewer = () => {
    setViewingImage(null);
  };

  // Filter submissions based on status and search
  const filteredSubmissions = submissions.filter(submission => {
    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
    const matchesSearch = !searchUserId || 
      submission.userId?.toLowerCase().includes(searchUserId.toLowerCase()) ||
      submission.username?.toLowerCase().includes(searchUserId.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  
  // Sort submissions by date
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSubmissions = sortedSubmissions.slice(indexOfFirstItem, indexOfLastItem);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, and pages around current
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div className="submissions">
        <div className="loading-spinner">Loading submissions...</div>
      </div>
    );
  }

  return (
    <div className="submissions">
      <div className="page-header">
        <h1>Receipt Ops</h1>
        <button className="btn-edit-rules" onClick={() => navigate('/rules')}>
          <IconEdit size={16} stroke={2} />
          Edit Rules Configuration
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab-button ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          Submissions
        </button>
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => navigate('/users')}
        >
          Users
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="submissions-container">
        <div className="submissions-controls">
          <div className="filter-section">
            <select 
              className="status-filter" 
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="processed">Processed</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="failed">Failed</option>
              <option value="partial_failed">Partial Failed</option>
              <option value="reward_failed">Reward Failed</option>
            </select>
          </div>
          
          <div className="search-section">
            <div className="search-box">
              <IconSearch className="search-icon" size={16} stroke={2} />
              <input
                type="text"
                placeholder="Search by user ID or username"
                value={searchUserId}
                onChange={(e) => {
                  setSearchUserId(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>

        {currentSubmissions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🧾</div>
            <h3>No Submissions Found</h3>
            <p>Try adjusting your filters or search criteria</p>
          </div>
        ) : (
          <>
            <div className="submissions-table-wrapper">
              <div className="table-header">
                <div className="th-date sortable" onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}>
                  Date & Time
                  <IconChevronDown 
                    className={`sort-icon ${sortOrder}`} 
                    size={16} 
                    stroke={2}
                  />
                </div>
                <div className="th-username">Username</div>
                <div className="th-userid">User ID</div>
                <div className="th-receipts">Receipts</div>
                <div className="th-status">Status</div>
                <div className="th-amount">Total Amount</div>
                <div className="th-reward">Reward Obtained</div>
                <div className="th-actions">Actions</div>
              </div>
              
              <div className="table-body">
                {currentSubmissions.map(submission => (
                  <div key={submission.id} className="table-row-card">
                    <div className="td-date">
                      <span className="date-cell">{formatDate(submission.createdAt)}</span>
                    </div>
                    <div className="td-username">
                      <span className="username-cell">{submission.username || '-'}</span>
                    </div>
                    <div className="td-userid">
                      <span className="user-id">{submission.userId}</span>
                    </div>
                    <div className="td-receipts">
                      <span className="receipt-count">
                        {submission.receipts?.length || 0} receipt(s)
                      </span>
                    </div>
                    <div className="td-status">
                      <span className={`status-badge ${getStatusBadge(submission.status)}`}>
                        {getStatusLabel(submission.status)}
                      </span>
                    </div>
                    <div className="td-amount">
                      <span className="amount">{formatAmount(submission.totalAmount)}</span>
                    </div>
                    <div className="td-reward">
                      <span className="reward-text">
                        {(() => {
                          // Check for voucher reward
                          const voucherName = submission.rewards?.voucher?.voucherName || 
                            submission.voucherSent?.ruleName;
                          const hasVoucher = voucherName || 
                            submission.rewards?.voucher?.nftId || 
                            submission.voucherSent?.voucherNftId;
                          
                          // Check for token reward
                          const tokenAmount = submission.rewards?.tokens?.amount;
                          const tokenType = submission.rewards?.tokens?.tokenType;
                          const hasToken = tokenAmount && tokenAmount > 0;
                          
                          // Display logic
                          if (hasVoucher && hasToken) {
                            // Both voucher and token
                            return `${voucherName || 'Voucher'} + ${tokenAmount} ${tokenType || 'Token'}`;
                          } else if (hasVoucher) {
                            // Only voucher
                            return voucherName || 'Voucher Sent';
                          } else if (hasToken) {
                            // Only token
                            return `${tokenAmount} ${tokenType || 'Token'}`;
                          } else {
                            return 'No reward';
                          }
                        })()}
                      </span>
                    </div>
                    <div className="td-actions">
                      <div className="action-buttons-cell">
                        <button
                          className="btn-view-icon"
                          onClick={() => openDetailModal(submission)}
                          title="View details"
                        >
                          <IconEye size={16} stroke={2} />
                        </button>
                        {canReview(submission) && (
                          <>
                            <button
                              className="btn-approve"
                              onClick={() => handleApprove(submission.id)}
                              disabled={approving || rejecting}
                            >
                              {approving ? '...' : 'Approve'}
                            </button>
                            <button
                              className="btn-reject"
                              onClick={() => handleReject(submission.id)}
                              disabled={approving || rejecting}
                            >
                              {rejecting ? '...' : 'Reject'}
                            </button>
                          </>
                        )}
                        {canResend(submission) && (
                          <button
                            className="btn-resend"
                            onClick={() => handleResend(submission.id)}
                            disabled={resending}
                          >
                            Resend
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  title="First page"
                >
                  <IconChevronsLeft size={16} stroke={2} />
                </button>
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  <IconChevronLeft size={16} stroke={2} />
                </button>
                
                <div className="pagination-numbers">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                    ) : (
                      <button
                        key={page}
                        className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  <IconChevronRight size={16} stroke={2} />
                </button>
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last page"
                >
                  <IconChevronsRight size={16} stroke={2} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Submission Details</h2>
              <button className="modal-close" onClick={closeDetailModal}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Submission Info</h3>
                <div className="detail-grid-left">
                  <div className="detail-item">
                    <label>SUBMISSION ID</label>
                    <span>{selectedSubmission.id}</span>
                  </div>
                  <div className="detail-item">
                    <label>USER ID</label>
                    <span>{selectedSubmission.userId}</span>
                  </div>
                  <div className="detail-item">
                    <label>DATE</label>
                    <span>{formatDate(selectedSubmission.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>STATUS</label>
                    <span className={`status-badge ${getStatusBadge(selectedSubmission.status)}`}>
                      {getStatusLabel(selectedSubmission.status)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>TOTAL AMOUNT</label>
                    <span className="amount-large">{formatAmount(selectedSubmission.totalAmount)}</span>
                  </div>
                  {selectedSubmission.reviewedBy && (
                    <div className="detail-item">
                      <label>REVIEWED BY</label>
                      <span>{selectedSubmission.reviewedBy}</span>
                    </div>
                  )}
                  {selectedSubmission.reviewedAt && (
                    <div className="detail-item">
                      <label>REVIEWED AT</label>
                      <span>{formatDate(selectedSubmission.reviewedAt)}</span>
                    </div>
                  )}
                  {selectedSubmission.rejectReason && (
                    <div className="detail-item full-width">
                      <label>REJECT REASON</label>
                      <span className="reject-reason">{selectedSubmission.rejectReason}</span>
                    </div>
                  )}
                </div>
                
                {/* Review Actions for Pending Submissions */}
                {canReview(selectedSubmission) && (
                  <div className="review-actions">
                    <p className="review-prompt">This submission is awaiting manual review.</p>
                    <div className="review-buttons">
                      <button
                        className="btn-approve-large"
                        onClick={() => handleApprove(selectedSubmission.id)}
                        disabled={approving || rejecting}
                      >
                        {approving ? 'Approving...' : '✓ Approve & Send Rewards'}
                      </button>
                      <button
                        className="btn-reject-large"
                        onClick={() => handleReject(selectedSubmission.id)}
                        disabled={approving || rejecting}
                      >
                        {rejecting ? 'Rejecting...' : '✗ Reject'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h3>Receipts ({selectedSubmission.receipts?.length || 0})</h3>
                <div className="receipts-grid">
                  {selectedSubmission.receipts?.map((receipt, index) => (
                    <div key={index} className="receipt-card">
                      {receipt.image && (
                        <img 
                          src={receipt.image} 
                          alt={`Receipt ${index + 1}`}
                          className="receipt-image"
                          onClick={() => openImageViewer(receipt.image)}
                        />
                      )}
                      <div className="receipt-info">
                        <span className="receipt-amount">{formatAmount(receipt.amount)}</span>
                        {receipt.date && (
                          <div className="receipt-detail">
                            <label>Receipt Date:</label>
                            <span>{new Date(receipt.date).toLocaleDateString()}</span>
                          </div>
                        )}
                        {receipt.storeName && (
                          <div className="receipt-detail">
                            <label>Store:</label>
                            <span>{receipt.storeName}</span>
                          </div>
                        )}
                        {receipt.storeNameOther && (
                          <div className="receipt-detail">
                            <label>Store Name:</label>
                            <span>{receipt.storeNameOther}</span>
                          </div>
                        )}
                        {receipt.ocrRaw && (
                          <details className="ocr-details">
                            <summary>OCR Text</summary>
                            <pre>{receipt.ocrRaw}</pre>
                          </details>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rewards Section */}
              <div className="detail-section">
                <h3>
                  Rewards
                  {selectedSubmission.rewards?.status && (
                    <span className={`rewards-status-badge ${selectedSubmission.rewards.status}`}>
                      {selectedSubmission.rewards.status === 'success' ? '✓ Success' :
                       selectedSubmission.rewards.status === 'partial_failed' ? '⚠ Partial' :
                       selectedSubmission.rewards.status === 'failed' ? '✗ Failed' : 
                       selectedSubmission.rewards.status}
                    </span>
                  )}
                </h3>
                
                {/* Voucher Status */}
                {(selectedSubmission.rewards?.expectedTypes?.includes('VOUCHER') || selectedSubmission.voucherSent) && (
                  <div className={`reward-block ${selectedSubmission.rewards?.voucherError ? 'error' : 'success'}`}>
                    <h4>🎟️ Voucher</h4>
                    {selectedSubmission.voucherSent || selectedSubmission.rewards?.voucher ? (
                      <div className="reward-details">
                        <div className="detail-item">
                          <label>Status</label>
                          <span className="reward-success">✓ Sent</span>
                        </div>
                        <div className="detail-item">
                          <label>Voucher Name</label>
                          <span>{selectedSubmission.rewards?.voucher?.voucherName || selectedSubmission.voucherSent?.ruleName || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                          <label>NFT ID</label>
                          <span>{selectedSubmission.voucherSent?.voucherNftId || selectedSubmission.rewards?.voucher?.nftId}</span>
                        </div>
                        {selectedSubmission.rewards?.voucherSentAt && (
                          <div className="detail-item">
                            <label>Sent At</label>
                            <span>{formatDate(selectedSubmission.rewards.voucherSentAt)}</span>
                          </div>
                        )}
                      </div>
                    ) : selectedSubmission.rewards?.voucherError ? (
                      <div className="reward-error-details">
                        <div className="detail-item">
                          <label>Status</label>
                          <span className="reward-failed">✗ Failed</span>
                        </div>
                        <div className="detail-item error-item">
                          <label>Error</label>
                          <span className="error-message">{selectedSubmission.rewards.voucherError}</span>
                        </div>
                        {selectedSubmission.rewards.attemptedVoucherName && (
                          <div className="detail-item">
                            <label>Attempted Voucher</label>
                            <span>{selectedSubmission.rewards.attemptedVoucherName}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="reward-pending-details">
                        <span className="reward-pending">Not processed</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Token Status */}
                {selectedSubmission.rewards?.expectedTypes?.includes('TOKEN') && (
                  <div className={`reward-block ${selectedSubmission.rewards?.tokenError ? 'error' : 'success'}`}>
                    <h4>🪙 Tokens</h4>
                    {selectedSubmission.rewards?.tokens && !selectedSubmission.rewards?.tokens?.pending ? (
                      <div className="reward-details">
                        <div className="detail-item">
                          <label>Status</label>
                          <span className="reward-success">✓ Sent</span>
                        </div>
                        <div className="detail-item">
                          <label>Amount</label>
                          <span>{selectedSubmission.rewards.tokens.amount} tokens</span>
                        </div>
                        <div className="detail-item">
                          <label>Token Name</label>
                          <span>{selectedSubmission.rewards.tokens.tokenName || selectedSubmission.rewards.tokens.tokenType || 'N/A'}</span>
                        </div>
                        {selectedSubmission.rewards?.tokensSentAt && (
                          <div className="detail-item">
                            <label>Sent At</label>
                            <span>{formatDate(selectedSubmission.rewards.tokensSentAt)}</span>
                          </div>
                        )}
                      </div>
                    ) : selectedSubmission.rewards?.tokenError ? (
                      <div className="reward-error-details">
                        <div className="detail-item">
                          <label>Status</label>
                          <span className="reward-failed">✗ Failed</span>
                        </div>
                        <div className="detail-item error-item">
                          <label>Error</label>
                          <span className="error-message">{selectedSubmission.rewards.tokenError}</span>
                        </div>
                        {selectedSubmission.rewards.attemptedTokenInfo && (
                          <div className="detail-item">
                            <label>Attempted Amount</label>
                            <span>{selectedSubmission.rewards.attemptedTokenInfo.amount} tokens</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="reward-pending-details">
                        <span className="reward-pending">Not processed</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Resend Button */}
                {canResend(selectedSubmission) && (
                  <div className="resend-section">
                    <button
                      className="btn-resend-large"
                      onClick={() => handleResend(selectedSubmission.id)}
                      disabled={resending}
                    >
                      {resending ? 'Resending...' : '🔄 Resend Failed Rewards'}
                    </button>
                    {selectedSubmission.resendAttempts?.length > 0 && (
                      <p className="resend-attempts">
                        Previous attempts: {selectedSubmission.resendAttempts.length}
                        {selectedSubmission.lastResendAt && (
                          <span> (Last: {formatDate(selectedSubmission.lastResendAt)})</span>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {selectedSubmission.error && (
                <div className="detail-section error-section">
                  <h3>System Error</h3>
                  <p className="error-text">{selectedSubmission.error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {viewingImage && (
        <div className="image-viewer-overlay" onClick={closeImageViewer}>
          <div className="image-viewer-content" onClick={e => e.stopPropagation()}>
            <button className="image-viewer-close" onClick={closeImageViewer}>
              <IconX size={20} stroke={2} />
            </button>
            <img 
              src={viewingImage} 
              alt="Receipt Full View"
              className="image-viewer-image"
            />
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowApproveModal(false); setPendingAction(null); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Approve Submission</h2>
            <p className="auth-modal-message">Are you sure you want to approve this submission? Rewards will be sent to the user.</p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowApproveModal(false); setPendingAction(null); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmApprove}
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowRejectModal(false); setPendingAction(null); setRejectReason(''); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Reject Submission</h2>
            <p className="auth-modal-message" style={{ marginBottom: '1rem' }}>Please enter a reason for rejection:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '0.75rem',
                border: '1px solid #cbd5e0',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '1rem'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowRejectModal(false); setPendingAction(null); setRejectReason(''); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmReject}
                style={{ 
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Confirmation Modal */}
      {showResendModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowResendModal(false); setPendingAction(null); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Resend Rewards</h2>
            <p className="auth-modal-message">Are you sure you want to resend the failed rewards?</p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowResendModal(false); setPendingAction(null); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmResend}
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Resend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal (Success/Error) */}
      {showMessageModal && (
        <div className="auth-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">{messageModalContent.title}</h2>
            <p className="auth-modal-message">{messageModalContent.message}</p>
            <button 
              className="auth-modal-button"
              onClick={() => setShowMessageModal(false)}
              style={{ 
                background: messageModalContent.title === 'Error' 
                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Submissions;

